import styled from 'styled-components';

export const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

export const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 50;
  background: ${({ theme }) => theme.colors.bgColor};
  padding-bottom: 12px;
  margin-bottom: 4px;
`;

export const Title = styled.h1`
  font-family: 'Nunito Sans', sans-serif;
  font-size: ${({ theme }) => theme.metrics.font.size.xl};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 20px;
`;

export const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 28px;
`;

export const StepDot = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  background: ${({ $active, $completed, theme }) =>
    $active ? theme.colors.primary.main :
    $completed ? '#16a34a' : '#E0E4E8'};
  color: ${({ $active, $completed }) => ($active || $completed) ? '#fff' : '#64748b'};
`;

export const StepLine = styled.div`
  flex: 1;
  height: 2px;
  background: ${({ $completed }) => $completed ? '#16a34a' : '#E0E4E8'};
`;

export const StepLabel = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $active, theme }) => $active ? theme.colors.text.primary : '#A0A8B4'};
`;

export const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

export const DropZone = styled.div`
  border: 2px dashed ${({ $dragOver, theme }) => $dragOver ? theme.colors.primary.main : '#D0D5DD'};
  border-radius: 6px;
  padding: 48px 24px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  background: ${({ $dragOver }) => $dragOver ? 'rgba(2, 145, 239, 0.04)' : 'transparent'};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.main};
  }
`;

export const DropText = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 12px;
`;

export const FileName = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const ErrorList = styled.div`
  margin-top: 16px;
  max-height: 300px;
  overflow-y: auto;
`;

export const ErrorItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 4px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  background: ${({ $severity }) => $severity === 'warning' ? '#FFF7ED' : '#FEF2F2'};
  color: ${({ $severity }) => $severity === 'warning' ? '#9A3412' : '#991B1B'};
`;

export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

export const SummaryCard = styled.div`
  padding: 16px;
  border-radius: 6px;
  background: ${({ $color }) => $color || '#F0F9FF'};
  text-align: center;
`;

export const SummaryValue = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const SummaryLabel = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 4px;
`;

export const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

export const Tag = styled.span`
  padding: 4px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: #E0F2FE;
  color: #0369A1;
`;

export const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
`;

export const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;

  th, td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid #E0E4E8;
  }

  th {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 12px;
  }
`;

export const StatusBadge = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === 'completed' ? '#DCFCE7' :
    $status === 'failed' ? '#FEE2E2' :
    $status === 'processing' ? '#FEF3C7' : '#F1F5F9'};
  color: ${({ $status }) =>
    $status === 'completed' ? '#166534' :
    $status === 'failed' ? '#991B1B' :
    $status === 'processing' ? '#92400E' : '#475569'};
`;

// Surfaces the active tenant so a holding admin doesn't accidentally upload
// rows to the wrong subsidiary after a tenant switch.
export const TargetBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #EFF6FF;
  border: 1px solid #BFDBFE;
  border-radius: 6px;
  margin-bottom: 16px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #1E3A8A;

  strong { font-weight: 700; }
`;

export const SectionTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 12px;
`;
